import React from 'react';
import * as Sentry from '@sentry/react';
import { TooltipProvider } from '@/components/ui/Tooltip.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Index from './pages/Index';
import { AuthProvider } from './contexts/AuthContext';
import { ConfirmationDialogProvider } from './components/ui/ConfirmDialog.Context.tsx';
import { Toaster } from '@/components/ui/Sonner.tsx';
import AuthLayout from '@/features/auth/signup/components/layouts/AuthLayout.tsx';
import dayjs from 'dayjs';
import 'dayjs/locale/pl.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import {
  createRoute,
  createRouter,
  RouterProvider,
} from '@tanstack/react-router';
import { loginRoute } from '@/features/auth/login/routes/loginRoute.ts';
import { signupRoute } from '@/features/auth/signup/routes/signupRoute.ts';
import { emailVerificationRoute } from '@/features/auth/signup/routes/emailVerificationRoute.ts';
import {
  completeCompanyDataRoute,
  completeProfileRoute,
  completeUserDetailsIndexRoute,
  completeUserDetailsRoute,
} from '@/features/auth/signup/routes/completeProfileRoutes.ts';
import { verificationFailedRoute } from '@/features/auth/signup/routes/verificationFailedRoute.ts';
import { workspacesRoute } from '@/features/auth/workspaces/routes/workspacesRoute.ts';
import {
  toursDashboardRoute,
  toursIndexRoute,
  toursRoute,
} from '@/features/dashboard/tours/routes/toursRoute.ts';
import {
  tourRoute,
  tourIndexRoute,
  tourSummaryRoute,
  tourParticipantsRoute,
  tourClientsRoute,
  tourWaitlistRoute,
  tourFinancesRoute,
  tourOptionsRoute,
  tourStatsRoute,
  tourMessagesRoute,
} from '@/features/dashboard/tours/routes/tourRoute.ts';
import {
  editTourBasicsRoute,
  tourConfigCostsRoute,
  tourConfigRoute,
  tourNewConfigBasicRoute,
  tourNewConfigRoute,
} from '@/features/dashboard/tourConfig/routes/tourConfigRoutes.ts';
import { tourConfigInstallmentsRoute } from '@/features/dashboard/tourConfig/routes/tourConfigInstallmentsRoute.ts';
import { bookingRoute } from '@/features/public/booking/routes/bookingRoutes.ts';
import { bookingConfirmationRoute } from '@/features/public/booking/routes/bookingConfirmationRoute.ts';
import { bookingTourRoute } from '@/features/public/booking/routes/bookingTourRoute.ts';
import { bookingSummaryRoute } from '@/features/public/booking/routes/bookingSummaryRoute.ts';
import { rootRoute } from '@/routing/rootRoute.ts';
import { participantsRoute } from '@/features/dashboard/participants/routes/participantsRoute.ts';
import {
  participantBookingRoute,
  participantIndexRoute,
  participantInfoRoute,
  participantRoute,
} from '@/features/dashboard/participants/routes/participantRoute.ts';
import {
  clientBookingRoute,
  clientIndexRoute,
  clientInfoRoute,
  clientRoute,
} from '@/features/dashboard/clients/routes/clientRoute.ts';
import { clientsRoute } from '@/features/dashboard/clients/routes/clientsRoute.ts';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { tourCustomFieldsRoute } from '@/features/dashboard/tourCustomFields/routes/tourCustomFieldsRoute.ts';
import { tourOptionsConfigRoute } from '@/features/dashboard/tourOptions/routes/tourOptionsConfigRoute.ts';
import { tourConsentsConfigRoute } from '@/features/dashboard/tourConsents/routes/tourConsentsConfigRoute.ts';
import { tourInsurancesConfigRoute } from '@/features/dashboard/tourInsurances/routes/tourInsurancesConfigRoute.ts';
import { tourTransportConfigRoute } from '@/features/dashboard/tourTransport/routes/tourTransportConfigRoute.ts';
import { insurancesRoute } from '@/features/dashboard/insurances/routes/insurancesRoute.ts';
import { financesRoute } from '@/features/dashboard/finances/routes/financesRoute.ts';
import { tfgRoute } from '@/features/dashboard/tfg/routes/tfgRoute.ts';
import { bookingsRoute } from '@/features/dashboard/bookings/routes/bookingsRoute.ts';
import {
  bookingDetailsBookingRoute,
  bookingDetailsConsentsRoute,
  bookingDetailsHistoryRoute,
  bookingDetailsIndexRoute,
  bookingDetailsInsurancesRoute,
  bookingDetailsMessagesRoute,
  bookingDetailsRoute,
} from '@/features/dashboard/bookingChanges/routes/bookingDetailsRoute.ts';
import { changePackageFlowRoute } from '@/features/dashboard/bookingChanges/routes/changePackageFlowRoute.ts';
import { waitlistRoute } from '@/features/dashboard/waitlist/routes/waitlistRoute.ts';
import { bookingNotFoundRoute } from '@/features/public/booking/routes/bookingNotFoundRoute.ts';
import { messagesRoute } from '@/features/dashboard/messages/routes/messagesRoute.ts';
import { settingsRoute } from '@/features/dashboard/companySettings/routes/settingsRoute.ts';
import { accessRoute } from '@/features/dashboard/access/routes/accessRoute.ts';
import { promoCodesRoute } from '@/features/dashboard/discounts/routes/promoCodesRoute.ts';
import { contractsRoute } from '@/features/dashboard/acceptedConsents/routes/contractsRoute.ts';
import { bookingPublicSummaryRoute } from '@/features/public/bookingSummary/routes/bookingPublicSummaryRoute.ts';
import {
  waitlistFormRoute,
  waitlistPublicRoute,
} from '@/features/public/waitlist/routes/waitlistPublicRoutes.ts';
import { acceptInvitationRoute } from '@/features/public/acceptInvitation/routes/acceptInvitationRoute.ts';
import { settingApprovalRoute } from '@/features/public/settingApproval/routes/settingApprovalRoute.ts';
import { AppErrorPage } from '@/features/dashboard/_root/components/AppErrorPage.tsx';

// Create a client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 5, // 5 minut (dawniej cacheTime)
      staleTime: 1000 * 60,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

dayjs().locale('pl');
dayjs.extend(customParseFormat);

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Index,
});

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth',
  component: AuthLayout,
});

const routeTree = rootRoute.addChildren([
  authRoute.addChildren([
    loginRoute,
    signupRoute,
    emailVerificationRoute,
    verificationFailedRoute,
    workspacesRoute,
    completeProfileRoute.addChildren([
      completeUserDetailsIndexRoute,
      completeUserDetailsRoute,
      completeCompanyDataRoute,
    ]),
  ]),
  appRoute.addChildren([
    toursIndexRoute,
    toursDashboardRoute,
    toursRoute,
    tourRoute.addChildren([
      tourIndexRoute,
      tourSummaryRoute,
      tourParticipantsRoute,
      tourClientsRoute,
      tourWaitlistRoute,
      tourFinancesRoute,
      tourOptionsRoute,
      tourStatsRoute,
      tourMessagesRoute,
    ]),
    tourNewConfigRoute.addChildren([tourNewConfigBasicRoute]),
    tourConfigRoute.addChildren([
      editTourBasicsRoute,
      tourConfigCostsRoute,
      tourTransportConfigRoute,
      tourOptionsConfigRoute,
      tourConfigInstallmentsRoute,
      tourCustomFieldsRoute,
      tourConsentsConfigRoute,
      tourInsurancesConfigRoute,
    ]),
    participantsRoute,
    participantRoute.addChildren([
      participantIndexRoute,
      participantInfoRoute,
      participantBookingRoute,
    ]),
    clientRoute.addChildren([
      clientIndexRoute,
      clientInfoRoute,
      clientBookingRoute,
    ]),
    clientsRoute,
    bookingsRoute,
    bookingDetailsRoute.addChildren([
      bookingDetailsIndexRoute,
      bookingDetailsBookingRoute,
      bookingDetailsConsentsRoute,
      bookingDetailsInsurancesRoute,
      bookingDetailsMessagesRoute,
      bookingDetailsHistoryRoute,
    ]),
    changePackageFlowRoute,
    waitlistRoute,
    financesRoute,
    tfgRoute,
    promoCodesRoute,
    contractsRoute,
    accessRoute,
    messagesRoute,
    settingsRoute,
    insurancesRoute,
  ]),
  bookingRoute.addChildren([bookingTourRoute, bookingSummaryRoute]),
  bookingNotFoundRoute,
  bookingConfirmationRoute,
  bookingPublicSummaryRoute,
  acceptInvitationRoute,
  settingApprovalRoute,
  waitlistPublicRoute.addChildren([waitlistFormRoute]),
  indexRoute,
]);

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: AppErrorPage,
});

Sentry.getClient()?.addIntegration(
  Sentry.tanstackRouterBrowserTracingIntegration(router),
);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ConfirmationDialogProvider>
            <RouterProvider router={router} />
            <Toaster />
          </ConfirmationDialogProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
